import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Grid,
  List,
  ListItem,
  ListIcon,
  Divider,
  useColorMode,
  Icon,
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import { getColor } from '../../../brandConfig';
import { NavbarWithCallToAction } from '../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns';

const PaulNiedererDiscoveryWorkshop: React.FC = () => {
  const { colorMode } = useColorMode();

  // Colors
  const cardGradientBg = colorMode === 'light'
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(248, 248, 248, 0.65) 50%, rgba(255, 255, 255, 0.7) 100%)'
    : 'linear-gradient(135deg, rgba(20, 20, 20, 0.7) 0%, rgba(30, 30, 30, 0.5) 50%, rgba(20, 20, 20, 0.7) 100%)';
  const cardBorder = colorMode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)';
  const textPrimary = getColor(colorMode === 'light' ? 'text.primary' : 'text.primaryDark', colorMode);
  const textSecondary = getColor(colorMode === 'light' ? 'text.secondary' : 'text.secondaryDark', colorMode);
  const primaryBlue = getColor('primary', colorMode);
  const successGreen = getColor('status.success', colorMode);

  const problemPoints = [
    'Blockchain tokens currently have NO legal standing in Australian courts',
    'RealT lost $2.72M when courts ruled token holders had zero legal protection',
    '92% of tokenization investors misunderstand what they actually own',
    'If the issuer goes bankrupt, token holders have no claim to the underlying asset',
  ];

  const solutionPoints = [
    'Dual-Register Architecture: Legal register (source of truth) + blockchain mirror',
    'CCIV Structure: Australia\'s newest investment vehicle with ASIC oversight',
    'Court-Enforceable Rights: Tokens correspond to legally-recognized beneficial interests',
    'Bankruptcy-Remote SPV: Asset protection through proper legal structuring',
    'ERC-3643 Compliance: Industry-standard security token with KYC/AML built-in',
  ];

  const workshopDeliverables = [
    'Technical architecture review of your tokenization requirements',
    'Dual-register system design tailored to your asset class',
    'Compliance pathway mapping (ASIC, AML/CTF, AFSL considerations)',
    'Smart contract specification outline (ERC-3643)',
    'Integration requirements with Corporate Director (Stratfund) systems',
    'Risk assessment and mitigation strategy',
    'Phased implementation roadmap',
    'Written discovery report with recommendations',
  ];

  return (
    <>
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        bg="transparent"
        minH="100vh"
        position="relative"
        overflow="hidden"
        width="100%"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: colorMode === 'light'
            ? 'radial-gradient(60% 60% at 0% 0%, rgba(248, 249, 250, 0.8) 0%, transparent 50%), radial-gradient(50% 50% at 50% 50%, #369eff33 0%, transparent 100%)'
            : 'radial-gradient(60% 60% at 0% 0%, rgba(17, 35, 52, 0.8) 0%, transparent 50%), radial-gradient(50% 50% at 50% 50%, #369eff33 0%, transparent 100%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <Container maxW="1000px" py={{ base: 8, md: 16 }} px={{ base: 4, md: 6 }} position="relative" zIndex={1}>

          {/* Above the Fold Hero */}
          <VStack spacing={{ base: 4, md: 6 }} align="center" mb={{ base: 8, md: 12 }}>
            <Badge
              colorScheme="teal"
              fontSize={{ base: "sm", md: "md" }}
              px={{ base: 3, md: 4 }}
              py={{ base: 1.5, md: 2 }}
              borderRadius="full"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              Discovery Workshop
            </Badge>

            <Heading
              fontSize={{ base: '2xl', sm: '3xl', md: '4xl', lg: '5xl' }}
              fontWeight="extrabold"
              textAlign="center"
              bgGradient="linear(to-r, teal.400, blue.500)"
              bgClip="text"
              px={{ base: 2, md: 0 }}
            >
              AGE Token Overlay Framework
            </Heading>

            <Text
              fontSize={{ base: 'lg', md: 'xl', lg: '2xl' }}
              color={textSecondary}
              textAlign="center"
              maxW="800px"
              fontWeight="medium"
            >
              Australia's First Legally-Enforceable Real Estate Tokenization System
            </Text>

            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              color={textSecondary}
              textAlign="center"
              maxW="700px"
              px={{ base: 2, md: 4 }}
            >
              A technical discovery session to assess your tokenization requirements
              and design a compliant implementation strategy
            </Text>

            <Box
              bg="linear-gradient(135deg, rgba(20, 184, 166, 0.2) 0%, rgba(59, 130, 246, 0.15) 100%)"
              px={{ base: 6, md: 10 }}
              py={{ base: 4, md: 6 }}
              borderRadius="xl"
              borderWidth={2}
              borderColor="teal.400"
              mt={4}
            >
              <VStack spacing={2}>
                <Text fontSize="sm" color={textSecondary} textTransform="uppercase" letterSpacing="wide">
                  Discovery Workshop Investment
                </Text>
                <Text fontSize={{ base: '4xl', md: '5xl' }} fontWeight="bold" color="teal.400">
                  $2,000
                </Text>
                <Text fontSize="sm" color={textSecondary}>
                  + GST | Fully credited toward implementation
                </Text>
              </VStack>
            </Box>
          </VStack>

          {/* The Problem */}
          <Box
            bg={cardGradientBg}
            backdropFilter="blur(20px)"
            p={{ base: 6, md: 8 }}
            borderRadius={{ base: "xl", md: "2xl" }}
            borderWidth={2}
            borderColor="red.300"
            shadow="xl"
            mb={{ base: 6, md: 10 }}
            width="100%"
          >
            <VStack spacing={6} align="stretch">
              <HStack spacing={3}>
                <WarningIcon color="red.400" boxSize={6} />
                <Heading size="lg" color={textPrimary}>
                  The Legal Certainty Crisis
                </Heading>
              </HStack>

              <Text fontSize={{ base: "md", md: "lg" }} color={textSecondary}>
                The $24B real estate tokenization market has a fundamental problem:
                <Text as="span" fontWeight="bold" color="red.400"> blockchain ownership does NOT equal property rights.</Text>
              </Text>

              <List spacing={3}>
                {problemPoints.map((point, idx) => (
                  <ListItem key={idx} display="flex" alignItems="flex-start">
                    <ListIcon as={WarningIcon} color="red.400" mt={1} />
                    <Text color={textSecondary}>{point}</Text>
                  </ListItem>
                ))}
              </List>
            </VStack>
          </Box>

          {/* The Solution */}
          <Box
            bg={cardGradientBg}
            backdropFilter="blur(20px)"
            p={{ base: 6, md: 8 }}
            borderRadius={{ base: "xl", md: "2xl" }}
            borderWidth={2}
            borderColor={successGreen}
            shadow="xl"
            mb={{ base: 6, md: 10 }}
            width="100%"
          >
            <VStack spacing={6} align="stretch">
              <HStack spacing={3}>
                <CheckCircleIcon color={successGreen} boxSize={6} />
                <Heading size="lg" color={textPrimary}>
                  The Torrens 2.0 Solution
                </Heading>
              </HStack>

              <Text fontSize={{ base: "md", md: "lg" }} color={textSecondary}>
                The AGE Token Overlay Framework creates <Text as="span" fontWeight="bold" color={successGreen}>legally-enforceable tokenization</Text> by
                ensuring blockchain records are synchronized with a legal register maintained by an AFSL-licensed Corporate Director.
              </Text>

              <List spacing={3}>
                {solutionPoints.map((point, idx) => (
                  <ListItem key={idx} display="flex" alignItems="flex-start">
                    <ListIcon as={CheckCircleIcon} color={successGreen} mt={1} />
                    <Text color={textSecondary}>{point}</Text>
                  </ListItem>
                ))}
              </List>

              <Box
                bg={colorMode === 'light' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.15)'}
                p={4}
                borderRadius="lg"
                borderWidth={1}
                borderColor={successGreen}
              >
                <Text textAlign="center" fontWeight="bold" color={successGreen}>
                  If blockchain and legal register conflict, the legal register prevails.
                  Courts recognize your ownership.
                </Text>
              </Box>
            </VStack>
          </Box>

          {/* Workshop Deliverables */}
          <Box
            bg={cardGradientBg}
            backdropFilter="blur(20px)"
            p={{ base: 6, md: 8 }}
            borderRadius={{ base: "xl", md: "2xl" }}
            borderWidth={2}
            borderColor={cardBorder}
            shadow="xl"
            mb={{ base: 6, md: 10 }}
            width="100%"
          >
            <VStack spacing={6} align="stretch">
              <Heading size="lg" color={textPrimary} textAlign="center">
                What You Get in the Discovery Workshop
              </Heading>

              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                {workshopDeliverables.map((item, idx) => (
                  <HStack key={idx} spacing={3} align="start">
                    <CheckCircleIcon color={primaryBlue} mt={1} flexShrink={0} />
                    <Text color={textSecondary}>{item}</Text>
                  </HStack>
                ))}
              </Grid>

              <Divider />

              <VStack spacing={3}>
                <Text fontSize="lg" fontWeight="bold" color={textPrimary}>
                  Workshop Format
                </Text>
                <Grid templateColumns={{ base: '1fr', sm: 'repeat(3, 1fr)' }} gap={4} w="full">
                  <VStack
                    bg={colorMode === 'light' ? 'gray.50' : 'rgba(255,255,255,0.05)'}
                    p={4}
                    borderRadius="lg"
                  >
                    <Text fontSize="2xl" fontWeight="bold" color={primaryBlue}>2-3</Text>
                    <Text fontSize="sm" color={textSecondary} textAlign="center">Hours of focused sessions</Text>
                  </VStack>
                  <VStack
                    bg={colorMode === 'light' ? 'gray.50' : 'rgba(255,255,255,0.05)'}
                    p={4}
                    borderRadius="lg"
                  >
                    <Text fontSize="2xl" fontWeight="bold" color={primaryBlue}>1</Text>
                    <Text fontSize="sm" color={textSecondary} textAlign="center">Written discovery report</Text>
                  </VStack>
                  <VStack
                    bg={colorMode === 'light' ? 'gray.50' : 'rgba(255,255,255,0.05)'}
                    p={4}
                    borderRadius="lg"
                  >
                    <Text fontSize="2xl" fontWeight="bold" color={primaryBlue}>100%</Text>
                    <Text fontSize="sm" color={textSecondary} textAlign="center">Credited if you proceed</Text>
                  </VStack>
                </Grid>
              </VStack>
            </VStack>
          </Box>

          {/* Who This Is For */}
          <Box
            bg={cardGradientBg}
            backdropFilter="blur(20px)"
            p={{ base: 6, md: 8 }}
            borderRadius={{ base: "xl", md: "2xl" }}
            borderWidth={2}
            borderColor={cardBorder}
            shadow="xl"
            mb={{ base: 6, md: 10 }}
            width="100%"
          >
            <VStack spacing={5} align="stretch">
              <Heading size="lg" color={textPrimary} textAlign="center">
                Ideal For
              </Heading>

              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                <VStack
                  align="start"
                  bg={colorMode === 'light' ? 'gray.50' : 'rgba(255,255,255,0.05)'}
                  p={5}
                  borderRadius="lg"
                  spacing={3}
                >
                  <Text fontWeight="bold" color={textPrimary}>Property Developers</Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Tokenize real estate projects with proper legal structure from day one
                  </Text>
                </VStack>

                <VStack
                  align="start"
                  bg={colorMode === 'light' ? 'gray.50' : 'rgba(255,255,255,0.05)'}
                  p={5}
                  borderRadius="lg"
                  spacing={3}
                >
                  <Text fontWeight="bold" color={textPrimary}>Fund Managers</Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Launch tokenized real estate funds with CCIV structure and ASIC compliance
                  </Text>
                </VStack>

                <VStack
                  align="start"
                  bg={colorMode === 'light' ? 'gray.50' : 'rgba(255,255,255,0.05)'}
                  p={5}
                  borderRadius="lg"
                  spacing={3}
                >
                  <Text fontWeight="bold" color={textPrimary}>Property Syndicators</Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Fractionalize property ownership with legally-enforceable token rights
                  </Text>
                </VStack>

                <VStack
                  align="start"
                  bg={colorMode === 'light' ? 'gray.50' : 'rgba(255,255,255,0.05)'}
                  p={5}
                  borderRadius="lg"
                  spacing={3}
                >
                  <Text fontWeight="bold" color={textPrimary}>Investment Platforms</Text>
                  <Text fontSize="sm" color={textSecondary}>
                    Build tokenized real estate marketplaces that pass regulatory scrutiny
                  </Text>
                </VStack>
              </Grid>
            </VStack>
          </Box>

          {/* About Paul */}
          <Box
            bg={cardGradientBg}
            backdropFilter="blur(20px)"
            p={{ base: 6, md: 8 }}
            borderRadius={{ base: "xl", md: "2xl" }}
            borderWidth={2}
            borderColor={cardBorder}
            shadow="xl"
            mb={{ base: 6, md: 10 }}
            width="100%"
          >
            <VStack spacing={4} align="center">
              <Heading size="lg" color={textPrimary}>
                Paul Niederer
              </Heading>
              <Badge colorScheme="teal" px={3} py={1}>
                Australia's Leading CCIV Expert
              </Badge>
              <Text fontSize={{ base: "md", md: "lg" }} color={textSecondary} textAlign="center" maxW="600px">
                300+ successful capital raises since 2015. Specialist in Corporate Collective Investment Vehicles
                and blockchain fractionalization with experience across Australasia, Asia, Europe, and the Middle East.
              </Text>
              <HStack spacing={4} pt={2}>
                <VStack>
                  <Text fontWeight="bold" color={primaryBlue}>RaiseWorth</Text>
                  <Text fontSize="sm" color={textSecondary}>Burleigh Heads, QLD</Text>
                </VStack>
                <Divider orientation="vertical" h="40px" />
                <VStack>
                  <Text fontWeight="bold" color={primaryBlue}>Since 2015</Text>
                  <Text fontSize="sm" color={textSecondary}>Mid-market focus</Text>
                </VStack>
              </HStack>
            </VStack>
          </Box>

          {/* CTA */}
          <Box
            bg="linear-gradient(135deg, rgba(20, 184, 166, 0.3) 0%, rgba(59, 130, 246, 0.2) 100%)"
            backdropFilter="blur(20px)"
            p={{ base: 6, md: 10 }}
            borderRadius={{ base: "xl", md: "2xl" }}
            borderWidth={3}
            borderColor="teal.400"
            shadow="2xl"
            textAlign="center"
            width="100%"
          >
            <VStack spacing={{ base: 4, md: 6 }}>
              <Heading size={{ base: "lg", md: "xl" }} color={textPrimary}>
                Ready to Explore Legal Tokenization?
              </Heading>

              <Text fontSize={{ base: "md", md: "lg" }} color={textSecondary} maxW="600px">
                Book a discovery workshop to assess your tokenization requirements
                and design a compliant implementation strategy.
              </Text>

              <VStack spacing={3} pt={2}>
                <Button
                  as="a"
                  href="mailto:paul@raiseworth.com?subject=AGE%20Token%20Overlay%20Discovery%20Workshop"
                  size="lg"
                  colorScheme="teal"
                  fontSize={{ base: "lg", md: "xl" }}
                  px={{ base: 6, md: 10 }}
                  py={{ base: 6, md: 7 }}
                  height="auto"
                >
                  Contact Paul Niederer
                </Button>

                <Text fontSize="sm" color={textSecondary}>
                  paul@raiseworth.com | +61 411 968 362
                </Text>
              </VStack>

              <Divider maxW="400px" />

              <Text fontSize="sm" color={textSecondary} fontStyle="italic">
                $2,000 + GST fully credited toward implementation if you proceed
              </Text>
            </VStack>
          </Box>

        </Container>
      </Box>

      <FooterWithFourColumns />
    </>
  );
};

export default PaulNiedererDiscoveryWorkshop;
